import { cloneDeep } from "lodash";

import { CategoryRecipeID } from "../../../app/homepage/homepage.types";
import { resizeImageIfCloudinary } from "../../../utils/images/cloudinaryUtils";
import { BANNER_TYPES, CATEGORY_ID_VALIDATE_REGEX, COMPONENTS } from "./constants";
import { BannerItem, Content, GroupItem, HomeScreen } from "./homepageJson.types";

export const jsonSanitizer = (homeJson: HomeScreen, filteredCategoryToRecipeId: CategoryRecipeID) => {

  const sanitizedData = cloneDeep(homeJson);
  let homePageRow = 0;
  let viewedHomePageRow = 0;

  if (!Array.isArray(sanitizedData.contents)) {
    console.error("Invalid JSON: 'contents' is missing");
  }

  sanitizedData.contents = sanitizedData.contents
    .filter((content: Content) => {
      if (!content.component || ![COMPONENTS.BANNER, COMPONENTS.CATEGORY, COMPONENTS.GROUP].includes(content.component)) return false;

      if (content.component === COMPONENTS.CATEGORY) return typeof content.category_id === 'string' && CATEGORY_ID_VALIDATE_REGEX.test(content.category_id);

      if (content.component === COMPONENTS.BANNER) {
        if (!Array.isArray(content.items)) return false;

        return content.items.every((item: BannerItem) => {
          if (![BANNER_TYPES.MANUAL, BANNER_TYPES.IMAGE].includes(item.type)) return false;

          if (item.type === BANNER_TYPES.MANUAL) {
            return item.manual && typeof item.manual.background_colour === 'string' &&
              typeof item.manual.title === 'string' &&
              typeof item.manual.subtitle === 'string' &&
              typeof item.manual.image === 'string' &&
              typeof item.manual.button_text === 'string';
          }

          if (item.type === BANNER_TYPES.IMAGE) return typeof item.image === 'string' && typeof item.action === 'string';

          return true;
        });
      }

      if (content.component === COMPONENTS.GROUP) {
        if (typeof content.title !== 'string' || !Array.isArray(content.items)) return false;

        return content.items.every((item: GroupItem) =>
          typeof item.title === 'string' &&
          typeof item.image === 'string' &&
          typeof item.action === 'string');
      }

      return true;
    })
    .map((content: Content) => {
      let newContent = { ...content, id: crypto.randomUUID(), homePageRow, viewedHomePageRow };
      homePageRow += 1;

      if (newContent.component === COMPONENTS.BANNER) {
        viewedHomePageRow += 1;
        newContent.items = newContent.items.map((item: BannerItem) => {
          const newItem = { ...item, id: crypto.randomUUID() };

          if (newItem.type === BANNER_TYPES.MANUAL) {
            const initialRecipeImageUrl = newItem.manual.image;
            const compactRecipeImageUrl = resizeImageIfCloudinary(initialRecipeImageUrl, 300, -1, -1, -1, true);
            newItem.manual = { ...newItem.manual, image: compactRecipeImageUrl, id: crypto.randomUUID() };
          } else {
            const initialBannerImageUrl = newItem.image;
            const compactBannerImageUrl = resizeImageIfCloudinary(initialBannerImageUrl, 343, -1, -1, -1, true);
            newItem.image = compactBannerImageUrl;
          }
          return newItem;
        });
      } else if (newContent.component === COMPONENTS.GROUP) {
        viewedHomePageRow += 1;
        newContent.items = newContent.items.map((item: GroupItem) => {
          const newItem = { ...item, id: crypto.randomUUID() };
          const initialGroupImageUrl = newItem.image;
          const compactGroupImageUrl = resizeImageIfCloudinary(initialGroupImageUrl, 300, -1, -1, -1, true);
          newItem.image = compactGroupImageUrl;
          return newItem;
        });
      } else if (newContent.component === COMPONENTS.CATEGORY) {
        newContent = { ...newContent, category_name: filteredCategoryToRecipeId[newContent.category_id]?.categoryName ?? "", recipe_ids: filteredCategoryToRecipeId[newContent.category_id]?.recipeIds ?? [] }
        if (newContent?.recipe_ids?.length !== 0) viewedHomePageRow += 1;
      }

      return newContent;
    });

  return sanitizedData;
};

export const isJsonString = (str: string | null) => {
  if (!str) return false;
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

const dietMap: { [key: string]: string[] } = {
  veg: ['Veg', 'Vegan'],
  vegan: ['Vegan'],
  egg: ['Veg', 'Vegan', 'Egg'],
  nonVeg: ['Non-Veg', 'Veg', 'Vegan', 'Egg'],
};

export function getDietArray(dietKey?: string): string[] {
  if (!dietKey) {
    return ['Non-Veg', 'Veg', 'Vegan', 'Egg'];
  }
  return dietMap[dietKey];
}
